import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
} from './guide-ui';

/**
 * Dutch body for "De loop sluiten: productielogs in je AI-IDE"
 * (localized counterpart of production-logs-in-your-ai-ide).
 */
export function ProductielogsAiIdeGuideBody() {
  return (
    <>
      <Lead>
        AI-coding-agents schrijven en shippen code sneller dan ooit, maar de
        feedback die vertelt of die code echt werkt woont nog ergens anders: in
        dashboards, terminals en geplakte stacktraces. Koppel je logplatform
        via het Model Context Protocol (MCP) aan Claude Code, Cursor of Codex
        en die loop sluit zich. De agent die de code schreef kan de
        productielogs lezen die de code produceert, zijn eigen deploy
        verifiëren en de fouten debuggen die hij zelf veroorzaakte, in dezelfde
        sessie.
      </Lead>

      <GuideSection id="de-kapotte-loop" title="De loop is kapot">
        <P>
          De typische AI-workflow van vandaag: je bouwt een feature met een
          agent, deployt, en verlaat dan de editor. Je opent een
          logging-dashboard, grept een container, of wacht tot een error
          tracker (of een gebruiker) meldt dat er iets stuk is. Vervolgens
          kopieer je een stacktrace, plakt die terug in de agent en vraagt hem
          een bug te fixen die hij niet in context kan zien.
        </P>
        <P>
          De agent is blind op precies de plek die er het meest toe doet. Hij
          kent elke regel code die hij schreef, maar weet niets over hoe die
          code zich in productie gedraagt. Elke rondgang via een dashboard is
          handmatige lijm tussen twee helften van dezelfde taak: code schrijven
          en code zien draaien.
        </P>
      </GuideSection>

      <GuideSection id="de-loop-sluiten" title="Wat de loop sluiten betekent">
        <P>
          <Term>De loop sluiten</Term> betekent de coding-agent dezelfde
          feedback geven als een senior engineer: directe toegang tot
          productielogs, foutpatronen en service-health. Met die toegang dekt
          één sessie de hele cyclus:
        </P>
        <OL>
          <LI>De agent schrijft de code en jij deployt.</LI>
          <LI>De agent doorzoekt de productielogs op nieuwe fouten sinds de deploy.</LI>
          <LI>Hij leest de echte stacktraces en legt ze naast de wijziging die hij net maakte.</LI>
          <LI>Hij schrijft de fix, jij deployt opnieuw, en hij verifieert in de logs dat de fouten weg zijn.</LI>
        </OL>
        <P>
          Geen tabbladen wisselen, geen stacktraces kopiëren, niet wachten op
          een bugmelding. De feedbackloop die vroeger tools en dagen omspande,
          draait binnen de editor in minuten.
        </P>
      </GuideSection>

      <GuideSection id="hoe-mcp-werkt" title="Hoe MCP productielogs in je IDE brengt">
        <P>
          <Term>MCP</Term> (Model Context Protocol) is het open protocol dat
          standaardiseert hoe AI-agents met externe systemen verbinden. Een
          logplatform met een MCP-server biedt <Term>getypeerde tools</Term>{' '}
          die de agent rechtstreeks aanroept, in plaats van een UI te scrapen
          of zelf API-calls te schrijven. De remote MCP-server van LogPulse is
          één HTTPS-endpoint waarmee Claude Code, Cursor, Codex of elke andere
          MCP-client één keer verbindt; daarna kan de agent tools aanroepen
          zoals:
        </P>
        <UL>
          <LI><Term>search_logs</Term>: productielogs doorzoeken met een gewone vraag of een LPQL-query.</LI>
          <LI><Term>count_patterns</Term>: fouten groeperen in patronen en zien welke nieuw zijn sinds een deploy.</LI>
          <LI><Term>compare_timeranges</Term>: vóór en na een release vergelijken om regressies te vinden.</LI>
          <LI><Term>timeline_analysis</Term>: zien wanneer een fout begon en hoe die zich verspreidde.</LI>
          <LI><Term>get_service_health</Term>: controleren of latency, foutpercentage en KPI&apos;s na de fix weer normaal zijn.</LI>
        </UL>
        <P>
          Omdat de tools binnen het platform draaien, komen antwoorden in
          seconden terug, begrensd tot jouw tenant en jouw rechten. De agent
          redeneert over echte data in plaats van te gokken op wat jij plakte.
          De volledige toolcatalogus en setup staan op de{' '}
          <GuideLink href="/mcp">MCP-serverpagina</GuideLink> en in de{' '}
          <GuideLink href="/docs/integrations/mcp-server">documentatie</GuideLink>.
        </P>
      </GuideSection>

      <GuideSection id="workflows" title="Vier workflows die de loop sluiten">
        <UL>
          <LI>
            <Term>Post-deploy-verificatie</Term>: vraag de agent na elke deploy
            om productie te controleren op nieuwe fouten. Hij doorzoekt de logs
            sinds de release, vergelijkt foutpatronen met het vorige venster en
            rapporteert alles wat nieuw is, vóór je gebruikers dat doen.
          </LI>
          <LI>
            <Term>Foutgedreven debuggen</Term>: in plaats van één geplakte
            stacktrace haalt de agent elke occurrence van de fout op, met de
            omliggende logregels en de request-context. Hij ziet de failure
            zoals productie die zag, en fixt daarna de code die hij al kent.
          </LI>
          <LI>
            <Term>Regressies opsporen</Term>: vraag &quot;is er iets trager of
            luidruchtiger geworden sinds v0.8?&quot; en de agent vergelijkt
            tijdvensters over releases heen, met foutvolume en latency naast je
            changelog.
          </LI>
          <LI>
            <Term>Van fout naar alert</Term>: is een klasse bugs gefixt, dan
            kan de agent er een alertregel of detectie voor voorstellen, zodat
            dezelfde fout nooit meer stilletjes shipt. Voorstellen komen in een
            wachtrij voor menselijke goedkeuring; de agent wijzigt nooit
            rechtstreeks configuratie.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="vibe-coding" title="Vibe-coded apps hebben productiefeedback het hardst nodig">
        <P>
          Hoe sneller code geschreven wordt, hoe meer het zwaartepunt van
          engineering verschuift van schrijven naar opereren. Apps die
          voornamelijk met AI-agents gebouwd zijn (&quot;vibe coding&quot;) maken dat
          acuut: wie de app shipt heeft de code misschien nooit gelezen, en
          gaat ook geen ruwe logbestanden lezen of Grafana-dashboards bouwen.
        </P>
        <P>
          Het praktische antwoord is de agent ook het opereren laten doen.
          Stuur de logs van de app naar een platform dat de agent via MCP kan
          bevragen, en &quot;waarom is mijn app stuk?&quot; wordt een vraag die de agent
          met bewijs beantwoordt: de echte fouten, wanneer ze begonnen, welke
          deploy ze veroorzaakte en wat er moet veranderen. Logging wordt het
          ontbrekende feedbackorgaan van AI-gebouwde software, geen
          ops-klusje dat er later bij komt.
        </P>
        <Callout title="Begin met loggen vóór je het nodig hebt">
          Een vibe-coded app zonder logs is ondebugbaar, voor mensen én agents.
          Gestructureerde logging toevoegen is één prompt voor dezelfde agent
          die de app bouwt; doe het in de eerste sessie, niet na de eerste
          storing.
        </Callout>
      </GuideSection>

      <GuideSection id="guardrails" title="Guardrails: geef de agent leestoegang, niet de sleutels">
        <P>
          Een autonome agent koppelen aan productiedata verdient dezelfde zorg
          als het onboarden van een nieuwe engineer. De regels die het veilig
          maken:
        </P>
        <UL>
          <LI><Term>Standaard read-only</Term>: de agent kan zoeken en analyseren, niets wijzigen.</LI>
          <LI><Term>Gescopete tokens</Term>: een personal access token met alleen de scopes die de workflow nodig heeft (voor de dev-loop is log-zoeken meestal genoeg).</LI>
          <LI><Term>Schrijfacties achter menselijke goedkeuring</Term>: alles wat de agent voorstelt (een alertregel, een pipeline) komt als uitgeschakeld concept in een reviewwachtrij.</LI>
          <LI><Term>Audittrail</Term>: elke toolcall wordt gelogd, gerate-limit en is herleidbaar tot het token dat hem deed.</LI>
        </UL>
        <P>
          De risico&apos;s (prompt injection via loginhoud, te brede scopes) en hoe
          een goed ontworpen MCP-gateway ze afvangt, staan uitgebreid in{' '}
          <GuideLink href="/guides/mcp-for-security">
            MCP for security: connecting AI agents to your logs
          </GuideLink>
          .
        </P>
      </GuideSection>

      <GuideSection id="aan-de-slag" title="Aan de slag">
        <OL>
          <LI>Stuur je applicatielogs naar LogPulse (HTTP-endpoint, agent of OTLP; de <GuideLink href="/docs/getting-started/quickstart">quickstart</GuideLink> kost zo&apos;n vijf minuten).</LI>
          <LI>Verbind je AI-IDE met de LogPulse MCP-server via one-click OAuth of een gescopet personal access token.</LI>
          <LI>Vraag de agent na je volgende deploy: &quot;check productie op nieuwe fouten sinds de deploy&quot;.</LI>
          <LI>Laat hem vinden, fixen en verifiëren. De loop is gesloten.</LI>
        </OL>
      </GuideSection>
    </>
  );
}
